import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import {
  collection,
  addDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import VoiceRecorder from "./VoiceRecorder";
import axios from "axios";
import { API_BASE_URL } from "../utils/api";
import { motion } from "framer-motion";
import logo from "../assets/images/logo.png";
import Swal from "sweetalert2";
import VideoComposer from "./VideoComposer";

/* ===============================
   Simple Image Lightbox component
   =============================== */
const ImageLightbox = ({ src, onClose }) => {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [drag, setDrag] = useState(null); // {x,y} start

  // prevent body scroll while open
  // useEffect(() => {
  //   const { overflow } = document.body.style;
  //   document.body.style.overflow = "hidden";
  //   return () => (document.body.style.overflow = overflow);
  // }, []);

  // mouse wheel zoom (desktop)
  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setScale((s) => {
      const ns = Math.min(5, Math.max(1, s + delta));
      if (ns === 1) setOffset({ x: 0, y: 0 });
      return ns;
    });
  };

  // mouse drag to pan
  const onMouseDown = (e) => {
    if (scale === 1) return;
    setDrag({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  };
  const onMouseMove = (e) => {
    if (!drag) return;
    setOffset({ x: e.clientX - drag.x, y: e.clientY - drag.y });
  };
  const onMouseUp = () => setDrag(null);

  // touch: drag or pinch
  const lastTouch = useRef(null);
  const lastPinch = useRef(null);

  const getDist = (t1, t2) => {
    const dx = t1.clientX - t2.clientX;
    const dy = t1.clientY - t2.clientY;
    return Math.hypot(dx, dy);
  };

  const onTouchStart = (e) => {
    if (e.touches.length === 1) {
      lastTouch.current = { x: e.touches[0].clientX - offset.x, y: e.touches[0].clientY - offset.y };
      lastPinch.current = null;
    } else if (e.touches.length === 2) {
      lastPinch.current = getDist(e.touches[0], e.touches[1]);
    }
  };

  const onTouchMove = (e) => {
    if (e.touches.length === 1 && lastTouch.current && scale > 1) {
      setOffset({
        x: e.touches[0].clientX - lastTouch.current.x,
        y: e.touches[0].clientY - lastTouch.current.y,
      });
    } else if (e.touches.length === 2) {
      const dist = getDist(e.touches[0], e.touches[1]);
      if (lastPinch.current) {
        const delta = (dist - lastPinch.current) / 200; // sensitivity
        setScale((s) => {
          const ns = Math.min(5, Math.max(1, s + delta));
          if (ns === 1) setOffset({ x: 0, y: 0 });
          return ns;
        });
      }
      lastPinch.current = dist;
    }
  };
  const onTouchEnd = () => {
    lastTouch.current = null;
    lastPinch.current = null;
  };

  const zoomIn = () => setScale((s) => Math.min(5, s + 0.25));
  const zoomOut = () => setScale((s) => {
    const ns = Math.max(1, s - 0.25);
    if (ns === 1) setOffset({ x: 0, y: 0 });
    return ns;
  });
  const reset = () => { setScale(1); setOffset({ x: 0, y: 0 }); };

  return (
    <div className="lb-overlay" onClick={onClose}>
      <div
        className="lb-stage"
        ref={containerRef}
        onClick={(e) => e.stopPropagation()} // keep clicks from closing
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <img
          src={src}
          alt="Preview"
          className="lb-image"
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
            touchAction: "none",
          }}
          draggable={false}
        />

        <div className="lb-toolbar" dir="ltr">
          <button className="lb-btn" onClick={zoomOut} title="Zoom out">−</button>
          <button className="lb-btn" onClick={zoomIn} title="Zoom in">+</button>
          <button className="lb-btn" onClick={reset} title="Reset">⟲</button>
          <a className="lb-btn" href={src} download target="_blank" rel="noreferrer" title="Download">⬇</a>
          <button className="lb-btn close" onClick={onClose} title="Close">✕</button>
        </div>
      </div>
    </div>
  );
};

const ImageComposer = ({ src, uploading, onCancel, onSend }) => {
  const [caption, setCaption] = useState("");

  const fireSend = () => !uploading && onSend?.(caption.trim());

  const handleKeyDown = (e) => {
    if (e.key === "Escape") onCancel?.();
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      fireSend();
    }
  };

  return (
    <div className="imgc-overlay" role="dialog" aria-modal="true" onClick={onCancel}>
      <div className="imgc-card" onClick={(e) => e.stopPropagation()}>
        {/* Stage holds the photo AND the close button */}
        <div className="imgc-stage">
          {/* X overlay on the image */}
          <button
            type="button"
            className="imgc-close"
            title="إغلاق"
            aria-label="إغلاق"
            onClick={onCancel}
            disabled={uploading}
          >
            <i className="bi bi-x-lg"></i>
          </button>

          <img src={src} alt="preview" className="imgc-photo" />
        </div>

        {/* Caption with send icon inside */}
        <div className="imgc-toolbar" dir="rtl">
          <div className="imgc-input">
            <textarea
              className="imgc-caption"
              rows={2}
              placeholder="اكتب رسالتك ..."
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button
              type="button"
              className="icon-btn send-icon imgc-send"
              aria-label="إرسال"
              title="إرسال"
              onClick={fireSend}
              disabled={uploading}
            >
              <i className="bi bi-send-fill fs-4"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};



const ChatRoom = ({ onBack }) => {
  const fileInputRef = useRef(null);
  const objectUrlRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [pickedType, setPickedType] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);

  const [chatId, setChatId] = useState(null);

  const currentUser = JSON.parse(localStorage.getItem("user"));
  const token = localStorage.getItem("token");
  const messagesEndRef = useRef(null);

  const [closing, setClosing] = useState(false);

  const hasPayload = text.trim().length > 0 || !!file;

  // NEW: lightbox state
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [showComposer, setShowComposer] = useState(false);
  const listRef = useRef(null);
  const [didInitialScroll, setDidInitialScroll] = useState(false);


  // in ChatRoom
  // useEffect(() => {
  //   const vv = window.visualViewport;
  //   if (!vv) return;

  //   const updateKb = () => {
  //     const kb = Math.max(0, window.innerHeight - vv.height); // keyboard height guess
  //     document.documentElement.style.setProperty('--kb', kb + 'px');
  //   };
  //   vv.addEventListener('resize', updateKb);
  //   updateKb();
  //   return () => vv.removeEventListener('resize', updateKb);
  // }, []);


  useEffect(() => {
    const fetchChatId = async () => {
      try {
        const res = await axios.post(
          `${API_BASE_URL}/chats`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setChatId(res.data?.chat?.id);
      } catch (error) {
        console.error("Failed to fetch chat ID:", error);
      }
    };
    fetchChatId();
  }, [token]);

  useEffect(() => {
    if (!chatId) return;
    const messagesRef = collection(db, "chats", String(chatId), "messages");
    const q = query(messagesRef, orderBy("createdAt"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setMessages(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, [chatId]);

  // useEffect(() => {
  //   setDidInitialScroll(false);
  // }, [chatId]);


  // Auto-scroll to bottom
  // useLayoutEffect(() => {
  //   const el = listRef.current;
  //   if (!el) return;

  //   // Scroll always on first load; afterwards only if the newest
  //   // message is ours (prevents yanking user while reading older msgs)
  //   const last = messages[messages.length - 1];
  //   const itsMine = last && last.senderId === (currentUser?.id || "guest");

  //   if (!didInitialScroll || itsMine) {
  //     // wait a frame so DOM height is correct
  //     requestAnimationFrame(() => {
  //       el.scrollTop = el.scrollHeight;
  //       setDidInitialScroll(true);
  //     });
  //   }
  // }, [messages.length, currentUser?.id, didInitialScroll]);


  // useEffect(() => {
  //   const setVh = () => {
  //     const vh = window.innerHeight * 0.01;
  //     document.documentElement.style.setProperty('--vh', `${vh}px`);
  //   };
  //   setVh();
  //   window.addEventListener('resize', setVh);
  //   return () => window.removeEventListener('resize', setVh);
  // }, []);

  // replace clearPickedFile with this
  const clearPickedFile = () => {
    setShowComposer(false);
    setFile(null);
    setPickedType(null);
    setUploadProgress(null);

    // revoke previous objectURL to avoid leaks / stale images
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setPreviewUrl(null);

    // let user choose the same file again
    if (fileInputRef.current) fileInputRef.current.value = "";
  };


  // ----- Core upload used by both sendMessage & composer -----
  const uploadAndSend = async ({ textPayload = "", fileObj = null, mediaKind = null }) => {
    if (!chatId) return;
    const messagesRef = collection(db, "chats", String(chatId), "messages");
    try {
      setUploading(true);
      setUploadProgress(null);

      // with file (image/video/audio/other)
      if (fileObj) {
        const fileRef = ref(storage, `chats/${Date.now()}_${fileObj.name}`);
        const uploadTask = uploadBytesResumable(fileRef, fileObj);

        await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (s) => setUploadProgress(Math.round((s.bytesTransferred / s.totalBytes) * 100)),
            reject,
            resolve
          );
        });

        const mediaUrl = await getDownloadURL(uploadTask.snapshot.ref);

        await addDoc(messagesRef, {
          text: textPayload,
          senderId: currentUser?.id || "guest",
          createdAt: serverTimestamp(),
          mediaUrl,
          mediaType: mediaKind || "file",
        });

        return;
      }

      // text-only
      if (textPayload.trim()) {
        await addDoc(messagesRef, {
          text: textPayload.trim(),
          senderId: currentUser?.id || "guest",
          createdAt: serverTimestamp(),
        });
      }
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault?.();
    if (!chatId) return;
    if (!text.trim() && !file) return;

    const messagesRef = collection(db, "chats", String(chatId), "messages");

    try {
      setUploading(true);
      setUploadProgress(null);

      if (file) {
        const fileRef = ref(storage, `chats/${Date.now()}_${file.name}`);
        const uploadTask = uploadBytesResumable(fileRef, file);

        await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progress = Math.round(
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100
              );
              setUploadProgress(progress);
            },
            reject,
            resolve
          );
        });

        const mediaUrl = await getDownloadURL(uploadTask.snapshot.ref);

        await addDoc(messagesRef, {
          text: text.trim(),
          senderId: currentUser?.id || "guest",
          createdAt: serverTimestamp(),
          mediaUrl,
          mediaType: pickedType || "file",
        });

        setText("");
        clearPickedFile();
        return;
      }

      await addDoc(messagesRef, {
        text: text.trim(),
        senderId: currentUser?.id || "guest",
        createdAt: serverTimestamp(),
      });
      setText("");
    } catch (err) {
      console.error("Message sending failed:", err);
      alert("تعذّر إرسال الرسالة.");
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  // ---- Image-composer send (caption + image) ----
  const handleComposerSend = async (captionText, mediaKind = "image") => {
    try {
      await uploadAndSend({
        textPayload: captionText || "",
        fileObj: file,
        mediaKind,
      });
      clearPickedFile();
    } catch (err) {
      console.error("Composer send failed:", err);
      alert(`تعذّر إرسال الـ ${mediaKind === "video" ? "فيديو" : "صورة"}.`);
    }
  };

  const handleVoiceSend = async (voiceFile) => {
    if (!chatId) {
      alert("لا توجد محادثة نشطة.");
      return;
    }
    try {
      setUploading(true);
      setUploadProgress(null);

      const messagesRef = collection(db, "chats", String(chatId), "messages");
      const fileRef = ref(storage, `chats/${Date.now()}_${voiceFile.name}`);
      const uploadTask = uploadBytesResumable(fileRef, voiceFile);

      await new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const progress = Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            );
            setUploadProgress(progress);
          },
          reject,
          resolve
        );
      });

      const mediaUrl = await getDownloadURL(uploadTask.snapshot.ref);

      await addDoc(messagesRef, {
        text: "",
        senderId: currentUser?.id || "guest",
        createdAt: serverTimestamp(),
        mediaUrl,
        mediaType: "audio",
      });
    } catch (err) {
      console.error("Voice send failed:", err);
      alert("تعذّر إرسال التسجيل الصوتي.");
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const handleCloseChat = async () => {
    if (!chatId) {
      await Swal.fire({
        icon: "warning",
        title: "لا توجد محادثة نشطة",
        text: "لا توجد محادثة نشطة لإغلاقها.",
        confirmButtonText: "حسناً",
        confirmButtonColor: "#CAEA3E",
        iconColor: "#CAEA3E",
        background: "#1B1B1B",
        color: "#FFFFFF",
      });
      return;
    }

    if (!token) {
      await Swal.fire({
        icon: "warning",
        title: "⚠️ يجب تسجيل الدخول",
        text: "يجب تسجيل الدخول لإغلاق المحادثة",
        confirmButtonText: "حسناً",
        confirmButtonColor: "#CAEA3E",
        iconColor: "#CAEA3E",
        background: "#1B1B1B",
        color: "#FFFFFF",
      });
      return;
    }

    if (closing) return;

    try {
      setClosing(true);
      await axios.get(`${API_BASE_URL}/chats/${chatId}/close`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      await Swal.fire({
        icon: "success",
        title: "✅ تم الإغلاق",
        text: "تم إغلاق المحادثة بنجاح.",
        confirmButtonText: "حسناً",
        confirmButtonColor: "#CAEA3E",
        iconColor: "#CAEA3E",
        background: "#1B1B1B",
        color: "#FFFFFF",
      });

      if (typeof onBack === "function") onBack();
    } catch (err) {
      console.error("Close chat failed:", err);
      const status = err?.response?.status;

      if (status === 401) {
        await Swal.fire({
          icon: "error",
          title: "انتهت الجلسة",
          text: "انتهت الجلسة. يرجى تسجيل الدخول.",
          confirmButtonText: "حسناً",
          confirmButtonColor: "#CAEA3E",
          iconColor: "#CAEA3E",
          background: "#1B1B1B",
          color: "#FFFFFF",
        });
      } else {
        await Swal.fire({
          icon: "error",
          title: "❌ خطأ",
          text: "لم نتمكن من إغلاق المحادثة. حاول مرة أخرى.",
          confirmButtonText: "حسناً",
          confirmButtonColor: "#CAEA3E",
          iconColor: "#CAEA3E",
          background: "#1B1B1B",
          color: "#FFFFFF",
        });
      }
    } finally {
      setClosing(false);
    }
  };

  // replace handleFilePick with this
  const handleFilePick = (selected) => {
    if (!selected) return;

    if (fileInputRef.current) fileInputRef.current.value = "";

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    setFile(selected);

    const url = URL.createObjectURL(selected);
    objectUrlRef.current = url;
    setPreviewUrl(url);

    const isImage =
      selected.type.startsWith("image/") ||
      /\.(jpe?g|png|gif|webp|bmp|heic|heif|tiff?)$/i.test(selected.name || "");

    if (isImage) {
      setPickedType("image");
      const img = new Image();
      img.onload = () => setShowComposer(true);
      img.onerror = () => setShowComposer(true);
      img.src = url;
      return;
    }

    if (selected.type.startsWith("video/")) {
      setPickedType("video");
      setShowComposer(true); // ✅ add this
      return;
    }

    if (selected.type.startsWith("audio/")) {
      setPickedType("audio");
      return;
    }

    setPickedType("file");
  };



  return (
    <div className="chat-page">
      {/* Header */}
      <div className="chat-header">
        <img src={logo} alt="Partech" className="chat-logo" />

        <div className="chat-header-actions chat-header-actions--stack">
          <motion.button
            className="back-btn"
            onClick={() => typeof onBack === "function" && onBack()}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            ارجع للخلف
          </motion.button>

          <motion.button
            type="button"
            className="close-chat-btn header-close"
            onClick={handleCloseChat}
            disabled={!chatId || closing}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="إغلاق المحادثة"
          >
            {closing ? "جارٍ الإغلاق..." : "إغلاق المحادثة"}
          </motion.button>
        </div>
      </div>


      {/* Messages */}
      <div className="chat-messages" ref={listRef}>
        {messages.map((msg) => {
          const isOwn = msg.senderId === currentUser?.id;
          return (
            <div key={msg.id} className={`chat-message ${isOwn ? "own" : "other"}`}>
              {msg.text && <div className="chat-bubble">{msg.text}</div>}

              {msg.mediaUrl && msg.mediaType === "image" && (
                <img
                  src={msg.mediaUrl}
                  alt="sent"
                  className="chat-media"
                  style={{ cursor: "zoom-in" }}
                  onClick={() => setLightboxSrc(msg.mediaUrl)}
                />
              )}

              {msg.mediaUrl && msg.mediaType === "video" && (
                <video src={msg.mediaUrl} controls className="chat-media" playsInline />
              )}

              {msg.mediaUrl && msg.mediaType === "audio" && (
                <audio controls className="chat-media" src={msg.mediaUrl} />
              )}

              {msg.mediaUrl && msg.mediaType === "file" && (
                <a href={msg.mediaUrl} target="_blank" rel="noreferrer" className="chat-file" download>
                  تنزيل الملف
                </a>
              )}
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input/Form */}
      <form onSubmit={sendMessage} className="chat-input-wrapper">
        {file && pickedType !== "image" && (
          <div className="preview-bar" dir="rtl">
            <div className="preview-left">
              {pickedType === "video" && <video src={previewUrl} className="preview-thumb" controls />}
              {pickedType === "audio" && <audio controls className="preview-audio" src={previewUrl} />}
              {pickedType === "file" && (
                <div className="preview-file">
                  <i className="bi bi-file-earmark"></i>
                  <span className="file-name">{file.name}</span>
                </div>
              )}
            </div>
            <button type="button" className="remove-file-btn" onClick={clearPickedFile}>
              إزالة
            </button>
          </div>
        )}

        <div className="chat-input-row">
          <div className="chat-input-box">
            <button type="button" className="btn btn-link px-2" tabIndex={-1}>
            </button>

            <input
              type="text"
              placeholder="اكتب رسالتك هنا"
              value={text}
              onChange={(e) => setText(e.target.value)}
              style={{ WebkitOverflowScrolling: "touch" }}
            />

            <label htmlFor="file-upload" className="icon-btn" title="إرفاق ملف">
              <i className="bi bi-paperclip fs-4"></i>
            </label>
            <input
              ref={fileInputRef}
              id="file-upload"
              type="file"
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xlsx,.zip"
              hidden
              onChange={(e) => handleFilePick(e.target.files?.[0])}
            />

            <button
              type="submit"
              className="icon-btn send-icon"
              aria-label="إرسال"
              disabled={uploading || !chatId}
              title="إرسال"
            >
              <i className="bi bi-send-fill fs-4"></i>
            </button>
          </div>

          {uploading && uploadProgress !== null && (
            <div className="upload-progress" aria-live="polite">
              <div className="progress-track">
                <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
              </div>
              <span className="progress-label">{uploadProgress}%</span>
            </div>
          )}
        </div>
      </form>

      {/* Image Lightbox for received images */}
      {lightboxSrc && <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}

      {/* NEW: Image Composer overlay for sending images */}
      {showComposer && pickedType === "image" && previewUrl && (
        <ImageComposer
          src={previewUrl}
          uploading={uploading}
          onCancel={clearPickedFile}
          onSend={handleComposerSend}
        />
      )}

      {showComposer && pickedType === "video" && previewUrl && (
        <VideoComposer
          src={previewUrl}
          uploading={uploading}
          onCancel={clearPickedFile}
          onSend={(caption) => handleComposerSend(caption, "video")}
        />
      )}
    </div>
  );
};

export default ChatRoom;
